import React, { useState } from "react";
import { TextField, Button, Select, MenuItem, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import { FirebaseConfig } from "../../../data/firebaseConfig";
import { allRoles, useAuthService } from "../../login/authentication";
import { toast } from "react-toastify";
import { User, UserStatus } from "./UsersPage";

interface InviteUsersProps {
    onUserInvited?: (user: User) => void;
}

export default function InviteUsers({ onUserInvited }: InviteUsersProps) {
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("viewer");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [open, setOpen] = useState(false);
    const functions = getFunctions(FirebaseConfig.app);
    const createInvitation = httpsCallable(functions, "createInvitation");

    const handleInvite = async () => {
        setLoading(true);
        try {
            if (!email || !role) {
                throw new Error("Email and role are required.");
            }

            const result = await createInvitation({
                email: email,
                role: role,
            });

            console.log("Firebase response:", result.data);

            toast.success("Invitation sent successfully.");
            setOpen(false);

            if (onUserInvited) {
                const data = result.data as { id: string };
                onUserInvited({ name: '', email: email, roles: [role], status: UserStatus.Pending, uid: data.id });
            }
        } catch (error) {
            // Catch and display the error message
            if (error instanceof Error) {
                setMessage(error.message);
            } else {
                setMessage("Failed to send invitation.");
            }
        }
        finally {
            setLoading(false);
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setMessage("");
    };

    return (
        <div>
            <Button variant="outlined" color="primary" onClick={handleClickOpen}>
                Invite User
            </Button>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Invite User</DialogTitle>
                <DialogContent>
                    <TextField
                        type="email"
                        label="Enter email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        margin="dense"
                        variant="outlined"
                    />
                    <Select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                    >
                        {Object.entries(allRoles).filter(([roleKey]) => roleKey !== 'viewer').map(([roleId, roleName]) => (
                            <MenuItem key={roleId} value={roleId}>
                                {roleName}
                            </MenuItem>
                        ))}
                    </Select>
                    {message && (
                        <Typography variant="body1" color="textSecondary" style={{ marginTop: 16 }}>
                            {message}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleInvite}
                        disabled={loading}
                        variant="contained"
                        color="primary"
                    >
                        {loading ? <CircularProgress size={24} /> : "Send Invitation"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
